// src/utils/customerExcel.js — import / export customers (name, phone, address, email) with Excel
export const CUSTOMER_SHEET = 'Customers';
export const CUSTOMER_INSTRUCTIONS = [
  ['How to import customers'],
  [''],
  ['1. Fill the "' + CUSTOMER_SHEET + '" sheet: one customer per row. Name, Phone and Address columns (Email is optional).'],
  ['2. Phone must be a 10-digit Indian mobile number. +91, 91 or a leading 0 are removed automatically.'],
  ['3. A phone number that is already saved is not added again. Blank cells in the sheet never erase saved details.'],
  ['4. Save the file, then in Admin Panel → Customers click "Upload Excel". You will see a preview before anything is saved.'],
  ['5. Tip: set the Phone column to Text format in Excel so long numbers are not shown as 9.87E+09.'],
];

export const normalizePhone = (raw) => {
  let d = String(raw ?? '').replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('091')) d = d.slice(3);
  else if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
};
export const isValidPhone = (d) => /^[6-9]\d{9}$/.test(d);

const squash = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const NAME_KEYS = ['name', 'customername', 'fullname', 'customer'];
const PHONE_KEYS = ['phone', 'phonenumber', 'mobile', 'mobilenumber', 'mobileno', 'phoneno', 'number', 'contact', 'contactnumber', 'whatsapp'];
const ADDR_KEYS = ['address', 'customeraddress', 'deliveryaddress', 'location'];
const MAIL_KEYS = ['email', 'emailid', 'mail', 'emailaddress'];
const pick = (keys, list) => keys.find(k => list.includes(squash(k)));

export const buildCustomerExportRows = (customers) =>
  [...customers]
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    .map(c => ({ 'Name': c.name || '', 'Phone': normalizePhone(c.phone) || String(c.phone || ''), 'Address': c.address || '', 'Email': c.email || '' }));

export const SAMPLE_CUSTOMER_ROWS = [{ 'Name': 'Example Customer', 'Phone': '9876543210', 'Address': 'Shop 1, Main Road, City', 'Email': '' }];

// rows: sheet rows as objects; existing: saved customer docs [{id, phone, name, address, email}]
export const parseCustomerRows = (rows, existing, { overwrite = false } = {}) => {
  const errors = [], creates = [], updates = [];
  let unchanged = 0, rowsRead = 0;
  if (!rows.length) return { creates, updates, errors: ['The sheet is empty.'], stats: { rowsRead: 0, unchanged: 0 } };

  const keys = Object.keys(rows[0]);
  const nameKey = pick(keys, NAME_KEYS), phoneKey = pick(keys, PHONE_KEYS), addrKey = pick(keys, ADDR_KEYS), mailKey = pick(keys, MAIL_KEYS);
  if (!phoneKey) return { creates, updates, errors: ['Could not find a "Phone" column. Please use the file downloaded from this page, or name the column Phone / Mobile.'], stats: { rowsRead: 0, unchanged: 0 } };

  const byPhone = new Map();
  existing.forEach(c => { const p = normalizePhone(c.phone); if (p && !byPhone.has(p)) byPhone.set(p, c); });
  const seen = new Set();

  rows.forEach((row, idx) => {
    const line = idx + 2;
    const rawPhone = row[phoneKey];
    const name = nameKey ? String(row[nameKey] ?? '').trim() : '';
    const address = addrKey ? String(row[addrKey] ?? '').trim() : '';
    const email = mailKey ? String(row[mailKey] ?? '').trim() : '';
    if (!String(rawPhone ?? '').trim() && !name && !address && !email) return; // blank row
    rowsRead++;

    const phone = normalizePhone(rawPhone);
    if (!isValidPhone(phone)) { errors.push(`Row ${line}: "${rawPhone}" is not a valid 10-digit mobile number${name ? ` (${name})` : ''}.`); return; }
    if (seen.has(phone)) { errors.push(`Row ${line}: ${phone} appears more than once in the file — only the first row is used.`); return; }
    seen.add(phone);

    const found = byPhone.get(phone);
    if (!found) { creates.push({ phone, name, address, email }); return; }

    const data = {}, changes = [];
    [['name', name], ['address', address], ['email', email]].forEach(([f, v]) => {
      if (!v) return;
      const cur = String(found[f] || '').trim();
      if (!cur) { data[f] = v; changes.push(f); }
      else if (overwrite && cur !== v) { data[f] = v; changes.push(f); }
    });
    if (changes.length) updates.push({ id: found.id, phone, name: found.name || name, data, changes });
    else unchanged++;
  });

  return { creates, updates, errors, stats: { rowsRead, unchanged } };
};